import { useEffect, useState } from "react";
import type { Field, Row } from "../types";
import { IconClose } from "../icons";
import SkillsInput from "./SkillsInput";
import CargoInput from "./CargoInput";
import ModeloPromocaoInput, { CriteriosPromocaoInput } from "./ModeloPromocaoInput";
import { cargoEhDoOperacional, type IndiceCargos } from "../cargos-operacional";
import { horaCurta } from "../format";
import { ordenarPorNome } from "../ordem";

interface Props {
  title: string;
  fields: Field[];
  initial?: Row | null;
  colaboradores: Row[]; // para selects com ref
  /** Cargos e Salários (cargo → departamento): decide quem tem competências. */
  indiceCargos?: IndiceCargos | null;
  onCancel: () => void;
  onSubmit: (data: Row) => Promise<void>;
}

/** Modal de cadastro/edição gerado a partir da definição de campos. */
export default function RecordForm({
  title,
  fields,
  initial,
  colaboradores,
  indiceCargos,
  onCancel,
  onSubmit,
}: Props) {
  const [form, setForm] = useState<Row>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const base: Row = {};
    for (const f of fields) {
      // Registro NOVO já vem com o padrão do campo (ex.: promoção nasce com
      // "atualizar o cadastro = Sim"); ao editar, vale o que está gravado.
      let v = initial ? initial[f.name] : (f.padrao ?? "");
      if (f.type === "date" && v) v = String(v).slice(0, 10); // YYYY-MM-DD
      base[f.name] = v ?? "";
    }
    setForm(base);
    setError(null);
  }, [initial, fields]);

  const set = (name: string, value: unknown) =>
    setForm((prev) => ({ ...prev, [name]: value }));

  /** Campos que o cargo por seleção consegue preencher sozinho, se existirem. */
  const temDepartamento = fields.some((f) => f.name === "departamento");
  /** Onde mora o modelo de promoção (para os critérios saberem qual é). */
  const campoModelo = fields.find((f) => f.type === "modeloPromocao")?.name;

  /**
   * Escolheu o colaborador: o formulário já traz a situação ATUAL dele.
   * Na aba Promoções é o que evita redigitar (e errar) o cargo e o salário de
   * onde a pessoa está saindo — o "antes" da promoção sai do próprio cadastro.
   * Só preenche campo em branco: nada do que foi digitado é sobrescrito.
   */
  function aoEscolherColaborador(id: unknown) {
    const c = colaboradores.find((x) => String(x.id) === String(id));
    if (!c) return;
    const de: Record<string, string> = {
      cargo_anterior: String(c.cargo ?? ""),
      salario_anterior: c.salario == null ? "" : String(c.salario),
    };
    setForm((prev) => {
      const next = { ...prev };
      for (const [campo, valor] of Object.entries(de)) {
        if (valor && campo in prev && !prev[campo]) next[campo] = valor;
      }
      return next;
    });
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      await onSubmit(form);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onMouseDown={onCancel}>
      <div className="modal-card" onMouseDown={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button type="button" className="icon-btn" onClick={onCancel}>
            <IconClose size={18} />
          </button>
        </div>

        {/* O rodapé fica FORA do form (preso embaixo, sem entrar na rolagem):
            o botão Salvar continua enviando este form pelo atributo `form`. */}
        <form id="record-form" className="modal-body" onSubmit={submit}>
          <div className="form-grid">
            {fields.map((f) => {
              // Campo preenchido pelo próprio sistema (ex.: o colaborador que a
              // contratação gerou): aparece só na tabela, nunca no formulário.
              if (f.soLeitura) return null;

              // Competências por marcação: é uma grade, então usa <div>
              // (um <label> só envolveria a grade inteira).
              //
              // Só aparece para quem tem CARGO do OPERACIONAL: a lista é toda
              // de serviço de campo (ensaio, montagem, subestação…) e não faz
              // sentido no cadastro do administrativo. O departamento do cargo
              // vem da tabela de Cargos e Salários, então o quadro aparece (ou
              // some) assim que o cargo é escolhido no popup.
              if (f.type === "skills") {
                if (!cargoEhDoOperacional(form.cargo, indiceCargos)) return null;
                return (
                  <div key={f.name} className="field field-wide">
                    <span>{f.label}</span>
                    <SkillsInput
                      value={String(form[f.name] ?? "")}
                      onChange={(next) => set(f.name, next)}
                    />
                  </div>
                );
              }

              // Cargo por seleção: abre o popup da tabela de Cargos e Salários
              // (também é <div>, porque um <label> em volta abriria o popup ao
              // clicar em qualquer canto do campo).
              if (f.type === "cargo") {
                // Onde o valor do nível é sugerido: "salario" no cadastro,
                // "salario_novo" na promoção (ver Field.preencheSalario).
                const campoSalario = f.preencheSalario ?? "salario";
                const temSalario = fields.some((x) => x.name === campoSalario);
                return (
                  <div key={f.name} className="field">
                    <span>
                      {f.label}
                      {f.required && <em className="req"> *</em>}
                    </span>
                    <CargoInput
                      value={String(form[f.name] ?? "")}
                      onChange={(cargo, salario, departamento) => {
                        set(f.name, cargo);
                        // O valor do nível e o departamento do cargo já entram
                        // preenchidos — mas SÓ com o campo em branco, para nunca
                        // sobrescrever o que a pessoa digitou (ou o salário que
                        // já foi combinado com o colaborador).
                        if (temSalario && salario != null && !form[campoSalario]) {
                          set(campoSalario, String(salario));
                        }
                        if (temDepartamento && departamento && !form.departamento) {
                          set("departamento", departamento);
                        }
                      }}
                    />
                  </div>
                );
              }

              // Modelo de promoção: a FORMA de promover, marcada na política da
              // empresa (src/promocoes-modelos.ts). Também é <div> pelo mesmo
              // motivo do cargo — um <label> em volta abriria o popup sozinho.
              if (f.type === "modeloPromocao") {
                return (
                  <div key={f.name} className="field field-wide">
                    <span>
                      {f.label}
                      {f.required && <em className="req"> *</em>}
                    </span>
                    <ModeloPromocaoInput
                      value={String(form[f.name] ?? "")}
                      onChange={(modelo) => {
                        set(f.name, modelo);
                        // Trocou o caminho: os critérios do caminho anterior
                        // não valem mais (cada modelo tem a sua régua).
                        if (modelo !== form[f.name]) set("criterios", "");
                      }}
                    />
                  </div>
                );
              }

              // Critérios do modelo escolhido, por marcação.
              if (f.type === "criteriosPromocao") {
                return (
                  <div key={f.name} className="field field-wide">
                    <span>{f.label}</span>
                    <CriteriosPromocaoInput
                      modeloNome={campoModelo ? String(form[campoModelo] ?? "") : ""}
                      value={String(form[f.name] ?? "")}
                      onChange={(texto) => set(f.name, texto)}
                    />
                  </div>
                );
              }
              return (
                <label
                  key={f.name}
                  className={f.type === "textarea" ? "field field-wide" : "field"}
                >
                  <span>
                    {f.label}
                    {f.required && <em className="req"> *</em>}
                  </span>
                  {renderInput(f, form[f.name], set, colaboradores, aoEscolherColaborador)}
                </label>
              );
            })}
          </div>
        </form>

        <div className="modal-foot">
          {error && <div className="form-error">{error}</div>}
          <button type="button" className="btn-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" form="record-form" className="btn-primary" disabled={saving}>
            {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}

function renderInput(
  f: Field,
  value: unknown,
  set: (name: string, value: unknown) => void,
  colaboradores: Row[],
  aoEscolherColaborador?: (id: unknown) => void,
) {
  const v = (value ?? "") as string | number;

  if (f.ref === "colaboradores") {
    return (
      <select
        value={v}
        required={f.required}
        onChange={(e) => {
          set(f.name, e.target.value);
          aoEscolherColaborador?.(e.target.value);
        }}
      >
        <option value="">Selecione...</option>
        {/* Select de colaborador de TODAS as abas genéricas: sempre A→Z. */}
        {ordenarPorNome(colaboradores, (c) => c.nome).map((c) => (
          <option key={c.id} value={c.id}>
            {c.nome}
          </option>
        ))}
      </select>
    );
  }

  switch (f.type) {
    case "select":
      return (
        <select
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        >
          <option value="">Selecione...</option>
          {f.options?.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      );
    case "textarea":
      return (
        <textarea
          rows={3}
          value={v}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "number":
      return (
        <input
          type="number"
          step="any"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "currency":
      return (
        <input
          type="number"
          step="0.01"
          min="0"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "date":
      return (
        <input
          type="date"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    // Hora do dia (HH:MM). O relógio do navegador já sai no formato brasileiro
    // de 24h; o valor vai para o banco como texto, sem fuso horário no meio —
    // 14:30 marcado é 14:30 gravado, venha de onde vier o acesso.
    case "time":
      return (
        <input
          type="time"
          value={horaCurta(v)}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "email":
      return (
        <input
          type="email"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    case "password":
      return (
        <input
          type="password"
          value={v}
          required={f.required}
          placeholder={f.placeholder}
          autoComplete="new-password"
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
    default:
      return (
        <input
          type="text"
          value={v}
          required={f.required}
          onChange={(e) => set(f.name, e.target.value)}
        />
      );
  }
}
